/** Ajutoare de UI pentru clipuri: etichete de stare, badge, cadru de preview/video. */
import type { CSSProperties, ReactNode } from 'react';
import type { ClipStatus, VideoFormat } from '@reclame-ai/shared';
import { VIDEO_FORMAT_META } from '@reclame-ai/shared';
import { Pill } from './ui';

export const CLIP_STATUS_LABEL: Record<ClipStatus, string> = {
  draft: 'Ciornă',
  scenariu_generare: 'Se scrie scenariul…',
  scenariu_generat: 'Scenariu de aprobat',
  scenariu_aprobat: 'Scenariu aprobat',
  imagini_generare: 'Se generează imaginile…',
  imagini_generate: 'Imagini de aprobat',
  imagini_aprobate: 'Imagini aprobate',
  video_generare: 'Se generează video…',
  audio: 'Se produce vocea…',
  montaj: 'Montaj în lucru…',
  preview: 'Preview gata',
  livrat: 'Livrat',
  ultra_briefing: 'Brief Ultra',
  ultra_editare: 'Editor lucrează',
  ultra_review: 'Review final',
  failed: 'Eroare tehnică',
  anulat: 'Anulat',
};

type Tone = 'accent' | 'ok' | 'warn' | 'err' | 'muted';

export function statusTone(status: ClipStatus): Tone {
  if (status === 'livrat') return 'ok';
  if (status === 'failed') return 'err';
  if (status === 'anulat') return 'muted';
  if (status === 'scenariu_generat' || status === 'imagini_generate' || status === 'preview' || status === 'ultra_review')
    return 'warn';
  return 'accent';
}

export function ClipStatusBadge({ status }: { status: ClipStatus }) {
  return <Pill tone={statusTone(status)}>{CLIP_STATUS_LABEL[status]}</Pill>;
}

/** Este clipul „în lucru" (procesare activă pe pipeline)? */
export function isProcessing(status: ClipStatus): boolean {
  return [
    'scenariu_generare',
    'imagini_generare',
    'video_generare',
    'audio',
    'montaj',
    'ultra_editare',
  ].includes(status);
}

export function aspectRatioCss(format: VideoFormat): string {
  const [w, h] = format.split(':');
  if (w && h) return `${w} / ${h}`;
  return String(VIDEO_FORMAT_META[format].aspectRatio);
}

/** Cadru vizual „ca un player" (fundal texturat) — folosit pentru preview/thumbnail. */
export function ClipFrame({
  format,
  children,
  watermark,
  style,
  maxWidth = 300,
}: {
  format: VideoFormat;
  children?: ReactNode;
  watermark?: boolean;
  style?: CSSProperties;
  maxWidth?: number;
}) {
  return (
    <div style={{ position: 'relative', width: `min(${maxWidth}px, 100%)`, margin: '0 auto', ...style }}>
      <div
        style={{
          position: 'relative',
          aspectRatio: aspectRatioCss(format),
          borderRadius: 20,
          overflow: 'hidden',
          backgroundColor: '#241C15',
          backgroundImage:
            'repeating-linear-gradient(135deg,rgba(255,255,255,.05) 0 11px,rgba(255,255,255,0) 11px 24px),linear-gradient(180deg,rgba(226,122,60,.36),rgba(10,6,4,.78))',
          boxShadow: '0 30px 70px -30px rgba(0,0,0,.7)',
          border: '1px solid var(--line)',
          display: 'grid',
          placeItems: 'center',
        }}
      >
        {children}
        {watermark && (
          <div
            aria-hidden="true"
            style={{
              position: 'absolute',
              inset: 0,
              display: 'grid',
              placeItems: 'center',
              transform: 'rotate(-24deg)',
              color: 'rgba(255,255,255,.16)',
              fontWeight: 800,
              letterSpacing: '.28em',
              fontSize: 15,
              pointerEvents: 'none',
              fontFamily: 'ui-monospace, Menlo, monospace',
            }}
          >
            PREVIEW · REC · PREVIEW · REC
          </div>
        )}
      </div>
      <span
        style={{
          position: 'absolute',
          top: -11,
          right: -8,
          background: 'var(--surface)',
          border: '1px solid var(--line)',
          borderRadius: 999,
          padding: '6px 11px',
          fontSize: 11.5,
          fontWeight: 800,
          fontVariantNumeric: 'tabular-nums',
        }}
      >
        {format}
      </span>
    </div>
  );
}
